/**
 * useChallenges Hook
 * Composes active (PENDING+ACTIVE) and lazy-loaded paginated history (COMPLETED).
 * History is only fetched when historyEnabled is true (user opens Finished tab or expands Finished section).
 */

import { useMemo, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useActiveChallengesQuery,
  useChallengesHistoryInfiniteQuery,
  useChallengesHistoryPreviewQuery,
} from './useChallengesQuery'
import { CHALLENGE_STATUS_CANCELLED } from '../constants/challenges.constants'
import type { ChallengeWithStats } from '../../../types/challenges'

interface UseChallengesProps {
  currentUserId: string
  isSignedIn: boolean
  isLoaded: boolean
  historyEnabled: boolean
}

interface UseChallengesReturn {
  challenges: ChallengeWithStats[]
  historyChallenges: ChallengeWithStats[]
  historyPreview: ChallengeWithStats[]
  isInitialLoadComplete: boolean
  isLoading: boolean
  isActiveError: boolean
  isLoadingHistory: boolean
  isLoadingHistoryPreview: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  loadChallenges: () => Promise<void>
  refetchActive: () => Promise<unknown>
  resetHistory: () => void
  fetchMoreHistory: () => Promise<unknown>
}

export const useChallenges = ({
  currentUserId,
  isSignedIn,
  isLoaded,
  historyEnabled,
}: UseChallengesProps): UseChallengesReturn => {
  const enabled = isSignedIn && isLoaded

  const {
    data: activeChallenges = [],
    isLoading: isLoadingActive,
    isFetched: isActiveFetched,
    isError: isActiveError,
    refetch: refetchActive,
  } = useActiveChallengesQuery(currentUserId, enabled)

  const {
    data: historyPreviewData = [],
    isLoading: isLoadingHistoryPreview,
    refetch: refetchHistoryPreview,
  } = useChallengesHistoryPreviewQuery(currentUserId, enabled)

  const queryClient = useQueryClient()
  const {
    data: historyData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isLoadingHistory,
    isFetched: isHistoryFetched,
    refetch: refetchHistory,
  } = useChallengesHistoryInfiniteQuery(currentUserId, enabled && historyEnabled)

  // When queries are disabled (guest/unauthenticated), mark as complete immediately
  const isInitialLoadComplete = !enabled
    ? true
    : historyEnabled
      ? isActiveFetched && isHistoryFetched
      : isActiveFetched

  const filterCompleted = useCallback(
    (list: ChallengeWithStats[]) => list.filter((c) => String(c.status).toUpperCase() !== CHALLENGE_STATUS_CANCELLED),
    []
  )

  const historyChallenges = useMemo(
    () => filterCompleted(historyData?.pages.flatMap((p) => p.data) ?? []),
    [historyData?.pages, filterCompleted]
  )

  const historyPreview = useMemo(
    () => filterCompleted(historyPreviewData as ChallengeWithStats[]),
    [historyPreviewData, filterCompleted]
  )

  const loadChallenges = useCallback(async () => {
    if (historyEnabled) {
      await Promise.all([refetchActive(), refetchHistoryPreview(), refetchHistory()])
    } else {
      await Promise.all([refetchActive(), refetchHistoryPreview()])
    }
  }, [refetchActive, refetchHistoryPreview, refetchHistory, historyEnabled])

  const resetHistory = useCallback(() => {
    queryClient.resetQueries({
      queryKey: ['challenges', 'history', currentUserId],
    })
  }, [queryClient, currentUserId])

  const fetchMoreHistory = useCallback(() => fetchNextPage(), [fetchNextPage])

  return {
    challenges: activeChallenges,
    historyChallenges,
    historyPreview,
    isInitialLoadComplete,
    isLoading: historyEnabled
      ? isLoadingActive || isLoadingHistory
      : isLoadingActive,
    isActiveError: enabled && isActiveError,
    isLoadingHistory,
    isLoadingHistoryPreview,
    isFetchingNextPage: isFetchingNextPage ?? false,
    hasNextPage: hasNextPage ?? false,
    loadChallenges,
    refetchActive,
    resetHistory,
    fetchMoreHistory,
  }
}
